import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Button,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Directions as DirectionsIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
} from '@mui/icons-material';
import productsService, { type SupplierPlant } from '../services/products';
import { useAuth } from '../context/AuthContext';
import { useMapView } from '../context/MapViewContext';
import { hasPermission } from '../utils/userPermissions';
import EmptyState from '../components/EmptyState';
import {
  HEADER_CELL_SX,
  ListToolbar,
  ListMap,
  NameCell,
  AddressCell,
  StatusDot,
  RowActionsMenu,
  DetailStrip,
  SelectableTableRow,
  SelectableCard,
  useRowSelection,
  hasCoords,
  directionsUrl,
  type MapPin,
  type RowAction,
} from '../components/ListPageShared';

type StatusFilter = 'all' | 'active' | 'inactive';

const BatchingPlants: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = hasPermission(user, 'batch_plants.create');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const mapView = useMapView();

  const [plants, setPlants] = useState<SupplierPlant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const selection = useRowSelection();

  const fetchPlants = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await productsService.listSupplierPlants();
      setPlants(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load batching plants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlants();
  }, []);

  // Reset to the first page when search or status changes
  useEffect(() => {
    setPage(0);
  }, [searchFilter, statusFilter]);

  const isActive = (plant: SupplierPlant) => plant.is_active !== false;
  const isMapped = (plant: SupplierPlant) => hasCoords(plant.latitude, plant.longitude);

  // Search applies first so the filter counts respect it
  const searchedPlants = useMemo(() => {
    if (!searchFilter.trim()) return plants;
    const q = searchFilter.toLowerCase();
    return plants.filter((p) =>
      p.name?.toLowerCase().includes(q) ||
      p.ready_mix_supplier?.toLowerCase().includes(q) ||
      p.address?.toLowerCase().includes(q)
    );
  }, [plants, searchFilter]);

  const filteredPlants = useMemo(() => {
    if (statusFilter === 'active') return searchedPlants.filter(isActive);
    if (statusFilter === 'inactive') return searchedPlants.filter((p) => !isActive(p));
    return searchedPlants;
  }, [searchedPlants, statusFilter]);

  // Keep the page in range when the list shrinks (e.g. after a toggle under a status filter)
  const lastPage = Math.max(0, Math.ceil(filteredPlants.length / pageSize) - 1);
  const currentPage = Math.min(page, lastPage);
  const pagedPlants = filteredPlants.slice(currentPage * pageSize, currentPage * pageSize + pageSize);

  const activeCount = searchedPlants.filter(isActive).length;

  // Pins for the map: plants in the current filtered result with coordinates
  const pins: MapPin[] = useMemo(
    () =>
      filteredPlants.filter(isMapped).map((plant) => ({
        id: plant.supplier_plant_id,
        position: [Number(plant.latitude), Number(plant.longitude)] as [number, number],
        popup: (
          <>
            <strong>{plant.name}</strong>
            <br />
            {plant.ready_mix_supplier && <>{plant.ready_mix_supplier}<br /></>}
            {plant.address && <>{plant.address}<br /></>}
            <Button
              size="small"
              sx={{ mt: 1, textTransform: 'none', p: 0 }}
              onClick={() => navigate(`/batching-plants/${plant.supplier_plant_id}`)}
            >
              {t('batchingPlant.viewPlant', 'View plant')}
            </Button>
          </>
        ),
      })),
    [filteredPlants, navigate, t]
  );

  /** Select a plant from the map: jump to its page and reveal its row. */
  const selectFromMap = (id: string) => {
    const idx = filteredPlants.findIndex((p) => p.supplier_plant_id === id);
    if (idx >= 0) setPage(Math.floor(idx / pageSize));
    selection.selectAndReveal(id);
  };

  const handleToggleActive = async (plant: SupplierPlant) => {
    const nextActive = !(plant.is_active !== false);
    setTogglingId(plant.supplier_plant_id);
    try {
      const updated = await productsService.updateSupplierPlant(plant.supplier_plant_id, {
        is_active: nextActive,
      });
      setPlants((prev) =>
        prev.map((p) => (p.supplier_plant_id === updated.supplier_plant_id ? updated : p))
      );
      setSnackbar({
        msg: nextActive
          ? t('batchingPlant.activated', 'Plant activated')
          : t('batchingPlant.deactivated', 'Plant deactivated'),
        severity: 'success',
      });
    } catch (e: any) {
      setSnackbar({
        msg: e.response?.data?.detail || t('batchingPlant.toggleFailed', 'Failed to update plant status'),
        severity: 'error',
      });
    } finally {
      setTogglingId(null);
    }
  };

  // -------------------------------------------------------------------------
  // Shared render fragments
  // -------------------------------------------------------------------------

  const editPlant = (plant: SupplierPlant) => navigate(`/batching-plants/${plant.supplier_plant_id}/edit`);

  const actionsFor = (plant: SupplierPlant) => {
    const directions = directionsUrl(plant.address, plant.latitude, plant.longitude);
    const view: RowAction = {
      key: 'view',
      label: t('users.viewDetails', 'View Details'),
      icon: <ViewIcon fontSize="small" />,
      onClick: () => navigate(`/batching-plants/${plant.supplier_plant_id}`),
    };
    const edit: RowAction | false = canManage && {
      key: 'edit',
      label: t('common.edit', 'Edit'),
      icon: <EditIcon fontSize="small" />,
      onClick: () => editPlant(plant),
    };
    const directionsAction: RowAction | false = !!directions && {
      key: 'directions',
      label: t('listPage.directions', 'Directions'),
      icon: <DirectionsIcon fontSize="small" />,
      href: directions,
    };
    const toggle: RowAction | false = canManage && {
      key: 'toggle',
      label: isActive(plant) ? t('batchingPlant.deactivate', 'Deactivate') : t('batchingPlant.activate', 'Activate'),
      icon: isActive(plant) ? <ToggleOffIcon fontSize="small" /> : <ToggleOnIcon fontSize="small" />,
      disabled: togglingId === plant.supplier_plant_id,
      onClick: () => handleToggleActive(plant),
    };
    return { menu: [view, edit, directionsAction, toggle], strip: [view, edit, directionsAction] };
  };

  const statusDot = (plant: SupplierPlant) => (
    <StatusDot active={isActive(plant)} activeLabel={t('common.active')} inactiveLabel={t('common.inactive')} />
  );

  const addressCell = (plant: SupplierPlant) => (
    <AddressCell
      address={plant.address}
      mapped={isMapped(plant)}
      onAdd={canManage ? () => editPlant(plant) : undefined}
    />
  );

  const detailStrip = (plant: SupplierPlant) => (
    <DetailStrip
      description={
        plant.address?.trim() ||
        t('batchingPlant.noAddressHint', 'No address yet. Add one to show this plant on the map.')
      }
      actions={actionsFor(plant).strip}
    />
  );

  const emptyState = (
    <EmptyState
      title={t('batchingPlant.noItems', 'No batching plants found')}
      description={t('batchingPlant.noItemsDescription', 'Create a new batching plant to get started.')}
      icon={<SearchIcon />}
      actionLabel={canManage ? t('batchingPlant.addNew', 'Add New') : undefined}
      onAction={canManage ? () => navigate('/batching-plants/new') : undefined}
    />
  );

  const paginationProps = {
    rowsPerPageOptions: [10, 20, 50, 100],
    component: 'div' as const,
    count: filteredPlants.length,
    rowsPerPage: pageSize,
    page: currentPage,
    onPageChange: (_: unknown, newPage: number) => setPage(newPage),
    onRowsPerPageChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setPageSize(parseInt(e.target.value, 10));
      setPage(0);
    },
  };

  if (loading && plants.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  const filterSummary =
    searchFilter || statusFilter !== 'all'
      ? t('batchingPlant.showingFiltered', 'Showing {{shown}} of {{total}} (filtered)', {
          shown: filteredPlants.length,
          total: plants.length,
        })
      : t('batchingPlant.totalCount', 'Total: {{total}}', { total: plants.length });

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <ListToolbar
        search={searchFilter}
        onSearch={setSearchFilter}
        searchPlaceholder={t('batchingPlant.searchPlaceholder', 'Search by plant name, supplier, address...')}
        statusOptions={[
          { key: 'all', label: t('batchingPlant.allPlants', 'All Plants'), count: searchedPlants.length },
          { key: 'active', label: t('common.active', 'Active'), count: activeCount },
          { key: 'inactive', label: t('common.inactive', 'Inactive'), count: searchedPlants.length - activeCount },
        ]}
        status={statusFilter}
        onStatus={setStatusFilter}
        onRefresh={fetchPlants}
        loading={loading}
        addLabel={canManage ? t('batchingPlant.addNew', 'Add New') : undefined}
        onAdd={() => navigate('/batching-plants/new')}
      />

      <ListMap
        pins={pins}
        selectedId={selection.selectedId}
        onSelect={selectFromMap}
        total={filteredPlants.length}
        missingAddress={filteredPlants.filter((p) => !p.address?.trim()).length}
        pinColor="#6B9A1F"
        selectedPinColor="#27500A"
        isMobile={isMobile}
        mobileOpen={mapView.open}
      />

      {/* Mobile card list */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {filteredPlants.length === 0 && !loading ? (
            <Paper sx={{ p: 3 }}>{emptyState}</Paper>
          ) : (
            pagedPlants.map((plant) => (
              <SelectableCard
                key={plant.supplier_plant_id}
                rowRef={selection.rowRef(plant.supplier_plant_id)}
                selected={selection.selectedId === plant.supplier_plant_id}
                onClick={() => selection.toggle(plant.supplier_plant_id)}
                header={
                  <>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <NameCell name={plant.name} subtitle={plant.ready_mix_supplier} />
                    </Box>
                    {statusDot(plant)}
                    <RowActionsMenu actions={actionsFor(plant).menu} />
                  </>
                }
                body={addressCell(plant)}
                detail={detailStrip(plant)}
              />
            ))
          )}
          <TablePagination
            {...paginationProps}
            labelRowsPerPage=""
            sx={{ '& .MuiTablePagination-selectLabel': { display: 'none' } }}
          />
        </Box>
      ) : (
        /* Desktop table: click a row to select it and show its detail strip */
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={HEADER_CELL_SX}>{t('batchingPlant.plant', 'Plant')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('batchingPlant.address', 'Address')}</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: 120 }}>{t('common.status', 'Status')}</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPlants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    {loading ? (
                      <Typography color="text.secondary">{t('common.loading')}</Typography>
                    ) : (
                      emptyState
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                pagedPlants.map((plant) => (
                  <SelectableTableRow
                    key={plant.supplier_plant_id}
                    rowRef={selection.rowRef(plant.supplier_plant_id)}
                    selected={selection.selectedId === plant.supplier_plant_id}
                    onClick={() => selection.toggle(plant.supplier_plant_id)}
                    colSpan={4}
                    detail={detailStrip(plant)}
                  >
                    <TableCell sx={{ maxWidth: 320 }}>
                      <NameCell name={plant.name} subtitle={plant.ready_mix_supplier} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 420 }}>{addressCell(plant)}</TableCell>
                    <TableCell>{statusDot(plant)}</TableCell>
                    <TableCell align="right" sx={{ pr: 1 }}>
                      <RowActionsMenu actions={actionsFor(plant).menu} />
                    </TableCell>
                  </SelectableTableRow>
                ))
              )}
            </TableBody>
          </Table>
          {/* Footer: filter summary on the left, pagination on the right */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', pl: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {filterSummary}
            </Typography>
            <TablePagination {...paginationProps} labelRowsPerPage={t('common.rowsPerPage')} />
          </Box>
        </TableContainer>
      )}

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
      >
        <Alert
          severity={snackbar?.severity ?? 'success'}
          onClose={() => setSnackbar(null)}
          variant="filled"
        >
          {snackbar?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BatchingPlants;
